import { MapPin, Clock, ChevronRight } from 'lucide-react';
import { ImageWithFallback } from '../figma/ImageWithFallback';
import type { Store } from '@/data';

interface PickupStoreInfoProps {
  selectedStore: Store;
  onStoreModalOpen: () => void;
  onHoursModalOpen: () => void;
  onCookingTimeModalOpen: () => void;
  translations: {
    storeLocationSelect: string;
    storeNames: Record<string, string>;
    storeStatus: Record<string, string>;
  };
}

export function PickupStoreInfo({
  selectedStore,
  onStoreModalOpen,
  onHoursModalOpen,
  onCookingTimeModalOpen,
  translations,
}: PickupStoreInfoProps) {
  const handleAddressClick = () => {
    const encodedAddress = encodeURIComponent(selectedStore.address);
    const googleMapsUrl = `https://maps.google.com/maps?q=${encodedAddress}`;
    window.open(googleMapsUrl, '_blank');
  };

  const getStatusColor = () => {
    switch (selectedStore.status) {
      case '영업중':
        return 'text-primary';
      case '준비중':
        return 'text-warning';
      default:
        return 'text-muted-foreground';
    }
  };

  return (
    <div className="flex gap-[clamp(8px,3vw,16px)] items-start">
      {/* 매장 이미지 */}
      <div className="relative w-[clamp(100px,26vw,150px)] h-[clamp(100px,26vw,150px)] shrink-0">
        <ImageWithFallback
          src={selectedStore.imageUrl}
          alt={selectedStore.name}
          className="w-full h-full object-cover object-center rounded-[clamp(6px,1.5vw,12px)] aspect-square"
        />
      </div>

      {/* 매장 정보 카드 */}
      <div className="flex-1 min-w-0 h-[clamp(100px,26vw,150px)]">
        <div className="bg-background rounded-[5px] p-[clamp(6px,1.5vw,12px)] [@media(min-width:500px)]:p-3 font-['Noto_Sans',sans-serif] h-[clamp(100px,26vw,150px)] flex min-w-0 flex-col justify-around">
          {/* 매장 위치 선택 버튼 */}
          <button
            onClick={onStoreModalOpen}
            className="w-full min-w-0 flex items-center justify-between p-[clamp(6px,1.5vw,12px)] [@media(min-width:500px)]:p-2 mb-[clamp(6px,1.5vw,12px)] [@media(min-width:500px)]:mb-2 bg-muted rounded-[5px] hover:bg-muted/80 transition-all duration-200 active:scale-95"
          >
            <div className="flex min-w-0 items-center justify-center gap-[clamp(4px,1vw,8px)] [@media(min-width:500px)]:gap-2">
              <MapPin className="h-[clamp(12px,3vw,16px)] [@media(min-width:500px)]:h-4 w-[clamp(12px,3vw,16px)] [@media(min-width:500px)]:w-4 text-primary shrink-0" />
              <span className="min-w-0 truncate text-[clamp(10px,2.5vw,14px)] [@media(min-width:500px)]:text-sm font-medium text-foreground">
                {translations.storeLocationSelect}
              </span>
            </div>
            <svg
              className="h-[clamp(10px,2.5vw,14px)] [@media(min-width:500px)]:h-3.5 w-[clamp(10px,2.5vw,14px)] [@media(min-width:500px)]:w-3.5 text-muted-foreground shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>

          {/* 매장 이름 (#61: API 데이터 우선 사용) */}
          <h1 className="w-full max-w-full min-w-0 overflow-hidden break-words text-[clamp(18px,4.55vw,23px)] [@media(min-width:500px)]:text-[23px] font-bold text-foreground [@media(min-width:500px)]:mb-2 leading-tight line-clamp-2 mt-[-4px]">
            {selectedStore.name || translations.storeNames[selectedStore.nameKey as keyof typeof translations.storeNames]}
          </h1>

          {/* 상태 및 준비 시간 */}
          <div className="flex items-center justify-between mb-[clamp(6px,1.5vw,12px)] [@media(min-width:500px)]:mb-2 gap-2">
            <div
              className={`cursor-pointer transition-all duration-200 hover:opacity-70 flex items-center gap-[clamp(3.2px,0.8vw,4.8px)] [@media(min-width:500px)]:gap-1 ${getStatusColor()}`}
              onClick={onHoursModalOpen}
            >
              <span className="text-[clamp(9.6px,2.4vw,12.8px)] [@media(min-width:500px)]:text-xs font-medium whitespace-nowrap">
                {translations.storeStatus[selectedStore.status] || selectedStore.status}
              </span>
              <ChevronRight className="w-[clamp(9.6px,2.4vw,12.8px)] [@media(min-width:500px)]:w-3 h-[clamp(9.6px,2.4vw,12.8px)] [@media(min-width:500px)]:h-3" />
            </div>
            {selectedStore.prep_time && (
              <div
                className="cursor-pointer transition-all duration-200 hover:opacity-70 flex items-center gap-[clamp(3.2px,0.8vw,4.8px)] [@media(min-width:500px)]:gap-1 shrink-0 text-primary"
                onClick={onCookingTimeModalOpen}
              >
                <Clock className="w-[clamp(9.6px,2.4vw,12.8px)] [@media(min-width:500px)]:w-3 h-[clamp(9.6px,2.4vw,12.8px)] [@media(min-width:500px)]:h-3" />
                <span className="text-[clamp(9.6px,2.4vw,12.8px)] [@media(min-width:500px)]:text-xs font-medium whitespace-nowrap">
                  {selectedStore.prep_time}
                </span>
                <ChevronRight className="w-[clamp(9.6px,2.4vw,12.8px)] [@media(min-width:500px)]:w-3 h-[clamp(9.6px,2.4vw,12.8px)] [@media(min-width:500px)]:h-3" />
              </div>
            )}
          </div>

          {/* 픽업 주소 */}
          <div
            className="flex items-start gap-[clamp(4px,1vw,8px)] [@media(min-width:500px)]:gap-2 cursor-pointer hover:text-primary transition-colors duration-200"
            onClick={handleAddressClick}
          >
            <MapPin className="h-[clamp(10px,2.5vw,14px)] [@media(min-width:500px)]:h-3.5 w-[clamp(10px,2.5vw,14px)] [@media(min-width:500px)]:w-3.5 text-muted-foreground shrink-0 mt-0.5" />
            <span className="min-w-0 text-[clamp(11px,2.8vw,13px)] [@media(min-width:500px)]:text-xs text-muted-foreground leading-tight line-clamp-1">
              {selectedStore.address}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
